import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CloseIcon from '@mui/icons-material/Close'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ImageIcon from '@mui/icons-material/Image'
import API from '../../../../../services/API'
import { GlobalContext } from '../../../../../App'
import { uploadDocument } from '../../../../../helpers/uploadDocument'

// Chip label per accepted MIME type. Keys double as the whitelist; their
// extensions must stay within contentTypeFromExtension() in
// server/src/helpers/s3.js so viewing serves the right type.
const DOCUMENT_FILE_TYPES = {
  'application/pdf': { extension: 'pdf' },
  'image/png': { extension: 'png' },
  'image/jpeg': { extension: 'jpg' },
  'image/gif': { extension: 'gif' },
  'image/webp': { extension: 'webp' },
}

const MAX_FILE_BYTES = 20 * 1024 * 1024 // 20 MB

const formatBytes = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`

const stripExtension = (name) => name.replace(/\.[^.]+$/, '')

const fileID = (file) => `${file.name}-${file.size}-${file.lastModified}`

// Upload PDFs or images to an employee's document record. Multiple files can
// be picked or dropped at once; each gets its own title (defaulted from the
// file name) and is saved as its own document. The caller owns the open
// state; onUploaded fires after any record is saved.
const AddNewDocument = ({
  open,
  onClose,
  employeeID,
  employeeFirstName,
  onUploaded,
}) => {
  const [items, setItems] = useState([]) // [{ id, file, title }]
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState({ index: 0, percent: 0 })
  const [skippedMessage, setSkippedMessage] = useState(null)
  const fileInputRef = useRef(null)
  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    if (!open) {
      setItems([])
      setDragging(false)
      setProgress({ index: 0, percent: 0 })
      setSkippedMessage(null)
    }
  }, [open])

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList ?? [])
    if (!incoming.length) return
    const fresh = incoming.filter(
      (file) =>
        DOCUMENT_FILE_TYPES[file.type] &&
        file.size <= MAX_FILE_BYTES &&
        !items.some((item) => item.id === fileID(file)),
    )
    const skipped = incoming.length - fresh.length
    setSkippedMessage(
      skipped > 0
        ? `${skipped} ${skipped === 1 ? 'file was' : 'files were'} skipped — only PDF or image files up to 20 MB can be added, once each.`
        : null,
    )
    setItems([
      ...items,
      ...fresh.map((file) => ({
        id: fileID(file),
        file,
        // A sensible default title saves a retype for most uploads
        title: stripExtension(file.name),
      })),
    ])
  }

  const updateTitle = (id, title) =>
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, title } : item)),
    )

  const removeItem = (id) =>
    setItems((current) => current.filter((item) => item.id !== id))

  const handleInputChange = (e) => {
    addFiles(e.target.files)
    // Reset so choosing the same file again still fires onChange
    e.target.value = ''
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    if (uploading) return
    addFiles(e.dataTransfer.files)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (uploading || !items.length) return
    if (items.some((item) => !item.title.trim())) return

    setUploading(true)
    const savedIds = []
    let failure = null
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      setProgress({ index: i, percent: 0 })
      try {
        const key = await uploadDocument(item.file, 'employees/documents', {
          onProgress: (percent) => setProgress({ index: i, percent }),
        })
        const saved = await API.post('/employee/document', {
          employeeID,
          title: item.title.trim(),
          s3key: key,
        })
        if (saved.status !== 200) {
          throw new Error('Unable to save the document. Please try again.')
        }
        savedIds.push(item.id)
      } catch (err) {
        console.error(err)
        failure = { item, message: err?.message }
        break // failures are usually systemic - don't spam one per file
      }
    }
    setUploading(false)

    if (savedIds.length) onUploaded?.()
    if (!failure) {
      setToast({
        message:
          savedIds.length === 1
            ? `${items[0].title.trim()} uploaded`
            : `${savedIds.length} documents uploaded`,
      })
      onClose()
    } else {
      // Saved files leave the list, so retrying can't create duplicates
      setItems((current) =>
        current.filter((item) => !savedIds.includes(item.id)),
      )
      setToast({
        message: `${failure.item.file.name}: ${
          failure.message || 'Unable to upload the document. Please try again.'
        }`,
        severity: 'error',
      })
    }
  }

  const titlesValid = items.every((item) => item.title.trim())

  return (
    <Dialog
      open={open}
      onClose={() => !uploading && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Add documents
        <Typography variant="body2" color="text.secondary">
          Uploaded to {employeeFirstName ?? 'this employee'}'s record
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={uploading}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={0.5}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={Object.keys(DOCUMENT_FILE_TYPES).join(',')}
            style={{ display: 'none' }}
            onChange={handleInputChange}
          />
          <Box
            role="button"
            tabIndex={0}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                fileInputRef.current?.click()
              }
            }}
            onDragOver={(e) => {
              e.preventDefault()
              if (!uploading) setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            sx={{
              borderRadius: 2,
              border: '1px dashed',
              borderColor: dragging ? 'anBlue.main' : 'grey.400',
              bgcolor: dragging ? 'grey.100' : 'grey.50',
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              cursor: uploading ? 'default' : 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: 'anBlue.main', bgcolor: 'grey.100' },
              // Children would otherwise fire dragleave as the pointer crosses them
              '& > *': { pointerEvents: 'none' },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 32, mb: 1, color: 'anBlue.main' }} />
            <Typography variant="body2" fontWeight={600}>
              Click to choose files, or drag them here
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PDF or image, up to 20 MB each
            </Typography>
          </Box>
          {skippedMessage && (
            <Typography variant="caption" color="error">
              {skippedMessage}
            </Typography>
          )}
          {items.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Titles are how the documents are listed on the record
            </Typography>
          )}
          {items.map((item, i) => {
            const FileIcon =
              item.file.type === 'application/pdf' ? PictureAsPdfIcon : ImageIcon
            return (
              <Stack
                key={item.id}
                direction="row"
                alignItems="center"
                gap={1.25}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  pl: 1.25,
                  pr: 0.5,
                  py: 1,
                }}
              >
                <Stack alignItems="center" gap={0.25} width={56} flexShrink={0}>
                  <FileIcon sx={{ color: 'anBlue.main' }} />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={DOCUMENT_FILE_TYPES[
                      item.file.type
                    ].extension.toUpperCase()}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatBytes(item.file.size)}
                  </Typography>
                </Stack>
                <TextField
                  label="Title"
                  size="small"
                  value={item.title}
                  onChange={(e) => updateTitle(item.id, e.target.value)}
                  required
                  fullWidth
                  autoComplete="off"
                  disabled={uploading}
                  error={!item.title.trim()}
                  helperText={
                    uploading
                      ? i < progress.index
                        ? 'Uploaded'
                        : i === progress.index
                          ? 'Uploading…'
                          : 'Waiting…'
                      : item.file.name
                  }
                />
                <IconButton
                  size="small"
                  onClick={() => removeItem(item.id)}
                  disabled={uploading}
                  aria-label={`Remove ${item.file.name}`}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            )
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!items.length || !titlesValid || uploading}
          startIcon={
            uploading ? (
              <CircularProgress
                variant={
                  progress.percent > 0 ? 'determinate' : 'indeterminate'
                }
                value={progress.percent}
                size={16}
                thickness={5}
                sx={{ color: 'inherit' }}
              />
            ) : (
              <CloudUploadIcon />
            )
          }
        >
          {uploading
            ? items.length === 1
              ? `Uploading… ${progress.percent}%`
              : `Uploading ${progress.index + 1} of ${items.length}… ${progress.percent}%`
            : items.length > 1
              ? `Upload ${items.length} documents`
              : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddNewDocument
